/**
 * Savings plan defaults and merge helpers.
 *
 * Plan marketing copy lives in Firestore (`settings/app.savingsPlans`) so an
 * admin can edit it without a deploy. These defaults are what the landing page
 * shows before anything has been saved.
 */

import type {
  SavingsPlanConfig,
  SavingsPlanId,
  SavingsPlanScriptureQuote,
} from '../types/firestore'

export const SAVINGS_PLAN_IDS: SavingsPlanId[] = ['umra', 'general']

/** Bump when default marketing copy changes — stale Firestore copy is replaced on the public site. */
export const SAVINGS_PLANS_COPY_VERSION = 2

const SHARED_SCRIPTURE: SavingsPlanScriptureQuote[] = [
  {
    reference: "Qur'an 2:275",
    arabic: 'وَأَحَلَّ اللَّهُ الْبَيْعَ وَحَرَّمَ الرِّبَا',
    text: 'Allah has permitted trade and forbidden riba (interest).',
  },
  {
    reference: "Qur'an 57:11",
    arabic: 'مَّن ذَا الَّذِي يُقْرِضُ اللَّهَ قَرْضًا حَسَنًا فَيُضَاعِفَهُ لَهُ',
    text:
      'Who is it that will lend Allah a goodly loan (qard hasan)? He will multiply it for him, and he will have a noble reward.',
  },
  {
    reference: 'Hadith · Muslim',
    text:
      'The Messenger of Allah (peace be upon him) cursed the one who consumes riba, the one who gives it, ' +
      'the one who records it, and the two witnesses to it — and said they are all equal.',
  },
]

export const DEFAULT_SAVINGS_PLANS: Record<SavingsPlanId, SavingsPlanConfig> = {
  umra: {
    enabled: true,
    title: 'Umra savings',
    tagline: 'Save towards Umra at your own pace — any amount, whenever you can',
    description: [
      'Open a savings account with the fund and put in whatever you can, whenever you can. There is no fixed monthly amount and no deadline.',
      'Your savings help provide interest-free (Qard Hasan) loans to people who need help. Every loan is secured against gold pledged with the fund — not riba or speculation.',
      'When you are ready for Umra, withdraw what you have saved. We keep a clear record of every deposit on your personal account.',
    ].join('\n\n'),
    highlights: [
      'Save any amount — small or large, it is up to you',
      'Pay in at the counter or by UPI, whenever it suits you',
      'No interest paid to you and none charged to anyone',
      'Your balance is recorded on a personal savings account',
      'Withdraw when you are ready for your journey',
      'You share in the reward of interest-free lending',
    ],
    scriptureQuotes: SHARED_SCRIPTURE,
    copyVersion: SAVINGS_PLANS_COPY_VERSION,
  },
  general: {
    enabled: true,
    title: 'General savings',
    tagline: 'Simple, flexible savings — any amount, anytime',
    description: [
      'Whether you are putting money aside for school fees, an emergency, or something else, you can save with the fund at your own pace.',
      'Pay in whatever you can, whenever you can. There is no fixed instalment and no minimum each month.',
      'Your savings support interest-free loans backed by gold. No riba — and you share in the reward of helping others borrow in a halal way.',
    ].join('\n\n'),
    highlights: [
      'Any amount, any time — you choose',
      'Deposit at the fund office or by UPI',
      'Take money out when you need it — speak to the fund first',
      'Personal statement on your account',
      'No interest — ever',
      'Helps the community access interest-free loans',
    ],
    scriptureQuotes: SHARED_SCRIPTURE,
    copyVersion: SAVINGS_PLANS_COPY_VERSION,
  },
}

export interface PublicSavingsPlan extends SavingsPlanConfig {
  id: SavingsPlanId
}

/** Serialise scripture quotes for the admin textarea (blocks separated by ---). */
export function scripturesToText(quotes: SavingsPlanScriptureQuote[] | undefined): string {
  if (!quotes?.length) return ''
  return quotes
    .map((q) => [q.reference, q.arabic, q.text].filter(Boolean).join('\n'))
    .join('\n---\n')
}

/** Parse admin textarea back into scripture quotes. */
export function textToScriptures(text: string): SavingsPlanScriptureQuote[] {
  return text
    .split(/\n---\n/)
    .map((block) => {
      const lines = block
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
      if (lines.length === 0) return null
      const reference = lines[0]!
      if (lines.length >= 3) {
        return { reference, arabic: lines[1], text: lines.slice(2).join('\n') }
      }
      return { reference, text: lines.slice(1).join('\n') }
    })
    .filter((q): q is SavingsPlanScriptureQuote => q !== null && q.text.length > 0)
}

export function mergeSavingsPlans(
  stored: Partial<Record<SavingsPlanId, Partial<SavingsPlanConfig>>> | undefined,
): Record<SavingsPlanId, SavingsPlanConfig> {
  return {
    umra: {
      ...DEFAULT_SAVINGS_PLANS.umra,
      ...stored?.umra,
      scriptureQuotes:
        stored?.umra?.scriptureQuotes ?? DEFAULT_SAVINGS_PLANS.umra.scriptureQuotes,
    },
    general: {
      ...DEFAULT_SAVINGS_PLANS.general,
      ...stored?.general,
      scriptureQuotes:
        stored?.general?.scriptureQuotes ?? DEFAULT_SAVINGS_PLANS.general.scriptureQuotes,
    },
  }
}

function isStaleCopy(stored: Partial<SavingsPlanConfig> | undefined): boolean {
  return (stored?.copyVersion ?? 0) < SAVINGS_PLANS_COPY_VERSION
}

/** Plan copy for the public site — ignores stale Firestore text and never shows fixed monthly amounts. */
function publicPlanConfig(
  id: SavingsPlanId,
  stored: Partial<Record<SavingsPlanId, Partial<SavingsPlanConfig>>> | undefined,
): SavingsPlanConfig {
  const merged = mergeSavingsPlans(stored)[id]
  const base = isStaleCopy(stored?.[id])
    ? { ...DEFAULT_SAVINGS_PLANS[id], enabled: merged.enabled }
    : merged

  const {
    suggestedMonthlyPaise: _monthly,
    suggestedDurationMonths: _duration,
    ...withoutSuggested
  } = base
  return withoutSuggested
}

export function planConfigForAdmin(
  id: SavingsPlanId,
  stored: Partial<Record<SavingsPlanId, Partial<SavingsPlanConfig>>> | undefined,
): SavingsPlanConfig {
  const merged = mergeSavingsPlans(stored)[id]
  if (isStaleCopy(stored?.[id])) {
    return { ...DEFAULT_SAVINGS_PLANS[id], enabled: merged.enabled }
  }
  return merged
}

export function publicSavingsPlans(
  stored: Partial<Record<SavingsPlanId, Partial<SavingsPlanConfig>>> | undefined,
  planOrder: SavingsPlanId[] = SAVINGS_PLAN_IDS,
): PublicSavingsPlan[] {
  return planOrder
    .filter((id) => publicPlanConfig(id, stored).enabled)
    .map((id) => ({
      id,
      ...publicPlanConfig(id, stored),
    }))
}

export function isSavingsPlanId(value: string): value is SavingsPlanId {
  return value === 'umra' || value === 'general'
}

export function planTitleFor(
  planId: SavingsPlanId,
  stored: Partial<Record<SavingsPlanId, Partial<SavingsPlanConfig>>> | undefined,
): string {
  return mergeSavingsPlans(stored)[planId].title
}
